import { rem } from "polished";

import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import type { ImageFit } from "@/components/editor/shared/header/header-settings-component";
import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import StyledButtonContainer from "@/components/editor/themes/hazel/shared/cta-button";
import { ColorLabels } from "@/components/editor/utils/use-color";

const Wrapper = withViewportStyle("div")`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  grid-column-gap: ${rem(128)};
  padding-left: ${rem(108)};

  ${(props) => props.$viewport.tablet`
    grid-template-columns: 1fr;
    grid-column-gap: unset;
    grid-row-gap: ${rem(32)};
    padding: ${rem(40)} ${rem(16)} ${rem(16)};
  `}
`;

const Content = withViewportStyle("div")`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${rem(24)};
  padding: ${rem(130)} 0;

  .text-primitive {
    text-align: left;
  }

  .text-primitive.headingOne {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  }

  .text-primitive.headingFour {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  }

  .text-primitive.paragraph {
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  }

  .ck-content h1,
  .ck-content h2,
  .ck-content h3,
  .ck-content h4,
  .ck-content h5,
  .ck-content p {
    margin: 0
  }

  ${(props) => props.$viewport.tablet`
    padding: 0;

    .text-primitive,
    .ck-content h1,
    .ck-content h2,
    .ck-content h3,
    .ck-content h4,
    .ck-content h5,
    .ck-content p {
      text-align: center;
    }
  `}
`;

const CTATextWrapper = withViewportStyle("div")`
.hazel .ck-content h1,
.hazel .ck-content h2,
.hazel .ck-content h3,
.hazel .ck-content h4,
.hazel .ck-content h5,
.hazel .ck-content p,
.hazel .ck-content li {
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
}
`;

const ImageWrapper = withViewportStyle("div")`
  position: relative;

  .semicircle {
    position: absolute;
    left: 0;
    bottom: 10%;

    &:first-of-type {
      bottom: calc(10% + ${rem(100)});
      transform-origin: right;
      transform: rotate(-90deg);
      opacity: 0.6;
    }

    ${(props) => props.$viewport.tablet`
      &:first-of-type  {
        bottom: calc(10% + ${rem(75)});
      }
    `}

    ${(props) => props.$viewport.mobile`
      left: -${rem(8)};

      &:first-of-type  {
        bottom: calc(10% + ${rem(50)});
      }
    `}
  }

  .circle {
    position: absolute;
    right: ${rem(128)};
    top: 15%;
    opacity: 0.6;

    ${(props) => props.$viewport.tablet`
      right: ${rem(98)};
    `}
    ${(props) => props.$viewport.mobile`
      right: ${rem(52)};
    `}
  }

  .quarter-circle {
    position: absolute;
    right: ${rem(8)};
    top: 15%;

    ${(props) => props.$viewport.mobile`
      right: -${rem(8)};
    `}
  }
`;

const Image = withViewportStyle("img")<{
  $position: ImagePosition;
  imageFit?: ImageFit;
}>`
  width: 100%;
  height: 100%;
  object-fit: ${({ imageFit }) => imageFit || "cover"};
  object-position: ${({ $position }) => $position};
`;

const LogoWrapper = withViewportStyle("div")`
  height: 100%;
  display: flex;
  align-items: center;

  ${(props) => props.$viewport.tablet`
    justify-content: center;
  `}
`;

const Logo = withViewportStyle("img")<{ isLogoResized: boolean }>`
  max-height: ${({ isLogoResized }) => (isLogoResized ? rem(120) : rem(40))};
  max-width: 100%;
  object-fit: contain;

  ${(props) => props.$viewport.tablet`
    max-height: ${({ isLogoResized }) => (isLogoResized ? rem(100) : rem(40))};
  `}

  ${(props) => props.$viewport.mobile`
    max-height: ${({ isLogoResized }) => (isLogoResized ? rem(80) : rem(40))};
  `}
`;

const ButtonWrapper = withViewportStyle(StyledButtonContainer)`
  ${(props) => props.$viewport.tablet`
    text-align: center;
  `}
`;

export {
  Wrapper,
  Content,
  CTATextWrapper,
  Logo,
  LogoWrapper,
  ImageWrapper,
  Image,
  ButtonWrapper,
};
